import { isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';

import viewInventory from '@/shared/api/inventory/viewInventory';
import getProduct from '@/shared/api/product/getProduct';
import useDataGridTable from '@/shared/components/dataDisplay/DataGridTable/hooks/useDataGridTable';
import useTableColumnVisibility from '@/shared/components/dataDisplay/DataGridTable/hooks/useTableColumnVisibility';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import Product from '@/shared/types/products/product.type';

import InventoryTableRow from '../types/inventoryTableRow.type';

const tableColumnsList = [
  'product_name',
  'variant_name',
  'available',
  'reserved',
  'total',
  'is_sell_out_of_stock',
  'actions',
];

type Args = {
  product: Product | null;
  refreshTableIndex?: number;
};

const useInventoryTable = ({ product, refreshTableIndex }: Args) => {
  const redirectLogin = useRedirectLogin();

  const { apiRef, model, requestBody } = useDataGridTable();
  const requestBodyRef = useRef<typeof requestBody | null>(null);

  const [table, setTable] = useState({
    data: [] as InventoryTableRow[],
    totalRows: 0,
    isLoading: false,
  });

  const { columns, visibilityModel, setVisibilityModel } =
    useTableColumnVisibility({
      tableColumnsList: tableColumnsList,
      tableRequiredColumns: tableColumnsList,
    });

  const productRef = useRef<Product | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!product) return;

      setTable((prev) => ({ ...prev, isLoading: true }));

      const responseProduct = await getProduct(
        product.product_id,
        redirectLogin,
      );

      if (responseProduct.success) {
        const productInUse = responseProduct.data;

        const assignedInventoryIds = productInUse.variants
          .filter(
            (variant) =>
              'inventory_id' in variant && variant.inventory_id !== null,
          )
          .map((item) => item.inventory_id);

        const response = await viewInventory(
          {
            search: [
              {
                inventory_id: {
                  cop: ['in'],
                  value: assignedInventoryIds,
                  op: 'and',
                },
              },
              ...requestBody.search,
            ],
          },
          redirectLogin,
        );

        setTable((prev) => ({ ...prev, isLoading: false }));

        if (response.success) {
          const { variants } = productInUse;
          const inventoryItems = response.data;

          const rows: InventoryTableRow[] = [];

          for (const variant of variants) {
            const inventoryItem = inventoryItems.find(
              (item) => item.inventory_id === variant.inventory_id,
            );

            if (inventoryItem) {
              const { variant_name } = variant;

              rows.push({
                ...inventoryItem,
                ...variant,
                variant_name: variant_name === '_default' ? '' : variant_name,
              });
            }
          }

          setTable((prev) => ({ ...prev, data: rows }));
        }
      }
    };

    if (
      !isEqual(requestBody, requestBodyRef.current) ||
      !isEqual(product, productRef.current) ||
      refreshTableIndex
    ) {
      productRef.current = product;
      requestBodyRef.current = requestBody;

      void fetchData();
    }
  }, [product, refreshTableIndex, redirectLogin, requestBody]);

  return {
    model,
    table,
    apiRef,
    columns,
    productRef,
    visibilityModel,
    setVisibilityModel,
  };
};

export default useInventoryTable;
